// src/pages/admin/AdminTransactions.jsx - COMPLETE
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  useMediaQuery,
  useTheme,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Refresh,
  Search,
  Visibility,
  Receipt,
  Download,
  Print,
  Clear,
} from "@mui/icons-material";

import {
  formatCurrency,
  formatDate,
  getStatusChipProps,
  getTransactionTypeIcon,
  getTransactionTypeLabel,
  calculateFinancialSummary,
} from "../../services/financeService";

import {
  FinancialSummaryCards,
  RevenueBreakdownBar,
} from "../../components/Finance/FinancialSummaryCards";

const TRANSACTION_TYPES = [
  { value: "all", label: "All Types" },
  { value: "deposit", label: "Deposit" },
  { value: "withdrawal", label: "Withdrawal" },
  { value: "payment", label: "Payment" },
  { value: "payout", label: "Payout" },
  { value: "refund", label: "Refund" },
  { value: "commission", label: "Commission" },
  { value: "order_payment", label: "Order Payment" },
];

const TRANSACTION_STATUS = [
  { value: "all", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const getStatusChip = (status) => {
  const props = getStatusChipProps(status);
  return (
    <Chip
      label={props.label}
      color={props.color}
      size="small"
    />
  );
};

const getTypeIcon = (type) => {
  return <span style={{ fontSize: '18px' }}>{getTransactionTypeIcon(type)}</span>;
};

const AdminTransactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    type: "all",
    status: "all",
    startDate: "",
    endDate: "",
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalRevenue: 0,
    totalCommission: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
    processingPayouts: 0,
    availableBalance: 0,
  });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page + 1,
        limit: pagination.limit,
        search: filters.search || undefined,
        type: filters.type !== "all" ? filters.type : undefined,
        status: filters.status !== "all" ? filters.status : undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      };

      const response = await adminApi.getTransactions(params);
      const data = response?.data || response || {};
      const transactionsList = data.transactions || data.data || [];
      const paginationData = data.pagination || data.meta || {};

      setTransactions(Array.isArray(transactionsList) ? transactionsList : []);
      setPagination({
        page: paginationData.page ? paginationData.page - 1 : 0,
        limit: paginationData.limit || 20,
        total: paginationData.total || 0,
      });

      const calculatedStats = calculateFinancialSummary(transactionsList);
      
      if (data.stats) {
        setStats({
          totalTransactions: data.stats.total || calculatedStats.totalTransactions,
          totalRevenue: data.stats.totalRevenue || calculatedStats.totalRevenue,
          totalCommission: data.stats.totalCommission || calculatedStats.totalCommission,
          totalPayouts: data.stats.totalPayouts || calculatedStats.totalPayouts,
          pendingPayouts: data.stats.pendingPayouts || calculatedStats.pendingPayouts,
          processingPayouts: data.stats.processingPayouts || calculatedStats.processingPayouts,
          availableBalance: data.stats.availableBalance || calculatedStats.availableBalance,
        });
      } else {
        setStats(calculatedStats);
      }
    } catch (error) {
      console.error("Error fetching admin transactions:", error);
      setError(error?.response?.data?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (event) => {
    setPagination((prev) => ({ ...prev, limit: parseInt(event.target.value, 10), page: 0 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailDialogOpen(true);
  };

  if (loading && transactions.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 12 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              All Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View all platform transactions across all vendors
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchTransactions}
              disabled={loading}
              size={isMobile ? "small" : "medium"}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              size={isMobile ? "small" : "medium"}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <FinancialSummaryCards stats={stats} loading={loading} variant="admin" />

      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <RevenueBreakdownBar stats={stats} />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search transactions..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleFilterChange("search", "")}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                label="Type"
              >
                {TRANSACTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                label="Status"
              >
                {TRANSACTION_STATUS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>Transaction ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No transactions found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((tx) => {
                  const type = tx.type?.toLowerCase() || '';
                  const isCredit = ['deposit', 'payment', 'order_payment', 'transfer_received'].includes(type);
                  const isCommission = type === 'commission';
                  
                  let color = 'error.main';
                  let prefix = '-';
                  if (isCredit) {
                    color = 'success.main';
                    prefix = '+';
                  } else if (isCommission) {
                    color = 'secondary.main';
                    prefix = '⚡';
                  }

                  return (
                    <TableRow key={tx.id || tx.transactionId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          #{tx.id || tx.transactionId || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {getTypeIcon(tx.type)}
                          <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                            {getTransactionTypeLabel(tx.type)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color={color}>
                          {prefix}{formatCurrency(tx.amount)}
                        </Typography>
                        {isCommission && tx.commissionRate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Rate: {tx.commissionRate}%
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {tx.vendorName || tx.storeName || "N/A"}
                      </TableCell>
                      <TableCell>{getStatusChip(tx.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize="12px">
                          {formatDate(tx.createdAt || tx.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(tx)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          rowsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedTransaction && (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">
                  Transaction #{selectedTransaction.id || "N/A"}
                </Typography>
                {getStatusChip(selectedTransaction.status)}
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body1" fontWeight="500">
                    {getTransactionTypeLabel(selectedTransaction.type)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Amount</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(selectedTransaction.amount)}
                  </Typography>
                </Grid>
                {selectedTransaction.commissionAmount && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Commission</Typography>
                    <Typography variant="body1" color="secondary.main">
                      {formatCurrency(selectedTransaction.commissionAmount)}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Vendor</Typography>
                  <Typography variant="body1">
                    {selectedTransaction.vendorName || selectedTransaction.storeName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                  <Typography variant="body1">
                    {selectedTransaction.paymentMethod || selectedTransaction.bank_type || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Reference</Typography>
                  <Typography variant="body1" fontSize="12px" sx={{ wordBreak: "break-all" }}>
                    {selectedTransaction.transactionId || selectedTransaction.external_id || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Created At</Typography>
                  <Typography variant="body1">
                    {formatDate(selectedTransaction.createdAt || selectedTransaction.created_at)}
                  </Typography>
                </Grid>
                {selectedTransaction.description && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body2">{selectedTransaction.description}</Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
              <Button variant="contained" startIcon={<Print />}>Print</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AdminTransactions;